import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Store, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtMoney } from "@/components/erp";
import { Th, Rozet, BosDurum } from "@/components/ortak/Liste";
import api, { errMsg } from "@/lib/api";

const trTarih = (d) => (d ? d.split("-").reverse().join(".") : "");
const KAYNAK = { bayi: ["Bayi portalı", Store], woo: ["WooCommerce", Globe] };

const KalemTermin = ({ k }) => {
  const o = k.onizleme || {};
  return (
    <tr className="border-t border-white/5 text-xs">
      <td className="p-2 font-mono">{k.kod}</td><td className="p-2">{k.ad}</td><td className="p-2 text-right font-mono">{k.miktar} {k.birim}</td>
      <td className="p-2 text-right font-mono">{o.stoktan ?? 0}</td>
      <td className="p-2 text-center">{o.uretim > 1e-9 ? <span className="text-violet-300">{o.uretim} üretim</span> : <span className="text-emerald-400">Stokta</span>}</td>
      <td className="p-2 text-center font-mono" data-testid={`gelen-termin-${k.kod}`}>{o.uretim > 1e-9 ? trTarih(o.termin) : "Hemen"}{o.yontem === "kalip" && <div className="text-[9px] text-muted-foreground">kalıp-kazan</div>}</td>
    </tr>
  );
};

const GelenKart = ({ s, onKarar }) => {
  const [ad, Ikon] = KAYNAK[s.kaynak] || [s.kaynak, Store];
  return (
    <div className="rounded-xl border border-white/10 p-4" data-testid={`gelen-${s.no}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Ikon className="w-4 h-4 text-[var(--g1)]" /><span className="font-semibold uppercase text-sm">{s.cari_unvan}</span><Rozet ton="mavi">{ad}{s.woo_no ? ` #${s.woo_no}` : ""}</Rozet>
        <span className="font-mono text-xs text-muted-foreground">{s.no} · {trTarih(s.tarih)}</span>
        <span className="ml-auto font-mono font-semibold">{fmtMoney(s.genel_toplam, s.para_birimi)}</span>
      </div>
      {s.notlar && <div className="text-xs text-muted-foreground mb-2">{s.notlar}</div>}
      <div className="overflow-x-auto"><table className="w-full min-w-[620px]"><thead><tr><Th>Kod</Th><Th>Ürün</Th><Th right>Miktar</Th><Th right>Stoktan</Th><Th>Durum</Th><Th>Termin</Th></tr></thead>
        <tbody>{s.kalemler.map((k, i) => <KalemTermin key={i} k={k} />)}</tbody></table></div>
      <div className="flex justify-end gap-2 mt-3">
        <button data-testid={`gelen-red-${s.no}`} onClick={() => onKarar(s, "gelen-red")} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg border border-red-500/40 text-red-300 text-xs font-semibold hover:bg-red-500/10"><X className="w-4 h-4" />Reddet</button>
        <button data-testid={`gelen-kabul-${s.no}`} onClick={() => onKarar(s, "kabul")} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold"><Check className="w-4 h-4" />Onayla, siparişlere kaydet</button>
      </div>
    </div>
  );
};

export const GelenSiparisler = ({ onClose, onDegisti }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const yukle = useCallback(() => api.get("/gelen-siparisler").then((r) => setD(r.data)), []);
  const cek = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post("/gelen-siparisler/cek");
      if (r.data.bagli) toast.success(`WooCommerce: ${r.data.eklenen} yeni sipariş alındı${r.data.atlanan.length ? `, ${r.data.atlanan.length} atlandı` : ""}`);
      r.data.atlanan.forEach((a) => toast.warning(`${a.no}: ${a.neden}`));
    } catch (e) { toast.error(errMsg(e)); }
    await yukle(); setBusy(false);
  }, [yukle]);
  useEffect(() => { cek(); }, [cek]);
  const karar = async (s, uc) => {
    if (uc === "gelen-red" && !window.confirm(`${s.no} reddedilsin mi?`)) return;
    try { const r = await api.post(`/siparisler/${s.id}/${uc}`, {}); toast.success(uc === "kabul" ? `${s.no} alınan siparişlere kaydedildi${r.data.durum === "onay_bekliyor" ? " (risk/fiyat onayına düştü)" : ""}` : `${s.no} reddedildi`); onDegisti(); yukle(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass max-w-5xl max-h-[90vh] overflow-y-auto erp-scroll" data-testid="gelen-dialog">
        <DialogHeader><DialogTitle className="font-head">Güncel Siparişler · onay bekleyen gelen siparişler</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Bayi portalından{d?.woo_bagli ? " ve WooCommerce sitesinden" : ""} gelen siparişler. Termin, kalıp-kazan döngüsü kapasitesine göre her kalem için hesaplanır.</span>
          {d && !d.woo_bagli && <Rozet ton="sari" testid="gelen-woo-yok">WooCommerce bağlı değil</Rozet>}
          <button data-testid="gelen-yenile" disabled={busy} onClick={cek} className="ml-auto inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-white/15 text-white"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />Yenile</button>
        </div>
        {!d ? <div className="py-10 text-center text-sm text-muted-foreground">Yükleniyor…</div> : d.siparisler.length === 0 ? <BosDurum testid="gelen-bos" text="Onay bekleyen gelen sipariş yok." />
          : <div className="space-y-3">{d.siparisler.map((s) => <GelenKart key={s.id} s={s} onKarar={karar} />)}</div>}
      </DialogContent>
    </Dialog>
  );
};
